import { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser, Check, X } from 'lucide-react';

export const SignaturePad = ({ 
  onSave, 
  onClear,
  initialSignature = null,
  label = "Sign Here",
  disabled = false 
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!initialSignature);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (initialSignature && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = initialSignature;
    }
  }, [initialSignature, isOpen]);

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Draw signature line
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    
    // Draw X
    ctx.fillStyle = '#1e40af';
    ctx.font = 'bold 24px Arial';
    ctx.fillText('X', 15, canvas.height - 35);
    
    // Reset for drawing
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;

    // Load initial signature if exists
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = initialSignature;
    }
  };

  const startDrawing = (e) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      initCanvas();
    }
    setHasSignature(false);
    if (onClear) onClear();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas && hasSignature) {
      const dataUrl = canvas.toDataURL('image/png');
      if (onSave) onSave(dataUrl);
      setIsOpen(false);
    }
  };

  const openPad = () => {
    setIsOpen(true);
    setTimeout(initCanvas, 100);
  };

  // Display saved signature or placeholder
  if (!isOpen) {
    return (
      <div 
        className={`relative border-2 border-dashed rounded-lg p-4 min-h-[120px] cursor-pointer transition-all ${
          hasSignature 
            ? 'border-success/50 bg-success/5' 
            : 'border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={disabled ? undefined : openPad}
        data-testid="signature-pad-trigger"
      >
        <div className={`absolute -top-3 left-3 text-xs font-bold px-3 py-1 rounded ${
          hasSignature ? 'bg-success text-success-foreground' : 'bg-primary text-primary-foreground'
        }`}>
          {hasSignature ? '✓ SIGNED' : label}
        </div>
        
        {initialSignature ? (
          <div className="flex items-center justify-center h-full pt-4">
            <img src={initialSignature} alt="Signature" className="max-h-[80px] object-contain" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full pt-4 text-muted-foreground">
            <span className="text-2xl font-bold text-primary mr-2">X</span>
            <p className="text-xs mt-2">Click to sign</p>
          </div>
        )}
      </div>
    );
  }

  // Signature pad modal
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setIsOpen(false)}>
      <div 
        className="bg-background rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Sign Here</h3>
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="border-2 border-primary/30 rounded-lg overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            width={450}
            height={200}
            className="w-full touch-none cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Use your mouse or finger to sign above
        </p>
        
        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={clearSignature} className="gap-2">
            <Eraser className="w-4 h-4" />
            Clear
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveSignature} disabled={!hasSignature} className="gap-2">
              <Check className="w-4 h-4" />
              Accept Signature
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
